/**
 * InboxesPage — shadcn/ui redesign (same pattern as ContactsPage)
 *
 * Visual changes (zero business-logic / API change):
 *   • Page wrapper matches ContactsPage: flex flex-1 flex-col gap-4 py-4 px-4 lg:px-6
 *   • Stat cards: gradient bg-gradient-to-t from-primary/5 to-card, icon in CardAction
 *   • Table card: search + channel-type filter in CardHeader > CardAction
 *   • Skeleton loading rows (no more full-page spinner)
 *   • Per-row hover quick-actions (Eye, Edit/Settings) + MoreVertical dropdown for Delete
 *   • Proper Delete confirmation Dialog (replaces window.confirm)
 *   • Toast notifications (no more inline Alert push)
 *   • Stats dialog: upgraded stat tiles matching page-level cards
 *   • Channel type picker dialog: improved grid with consistent hover
 *   • Empty state with icon + CTA button
 *   • Table pagination (client-side on filtered list): rows per page, range, first/prev/next/last
 *   • Bounded table height + sticky header (matches Contacts table UX)
 *
 * All API calls, state, CRUD, channel config dialogs, and AI toggle are unchanged.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Settings,
  MessageSquare,
  Mail,
  Phone,
  Globe,
  Bot,
  Plug,
  Eye,
  MoreVertical,
  Search,
  Inbox,
  Layers,
  UserCheck,
  AlertTriangle,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import api, { inboxesAPI } from '../services/api';
import WhatsAppChannelConfig from '../components/WhatsAppChannelConfig';
import InstagramChannelConfig from '../components/InstagramChannelConfig';
import APIChannelConfig from '../components/APIChannelConfig';
import EmailChannelConfig from '../components/EmailChannelConfig';
import FacebookChannelConfig from '../components/FacebookChannelConfig';
import SMSChannelConfig from '../components/SMSChannelConfig';
import CustomGPTChannelConfig from '../components/CustomGPTChannelConfig';
import WebsiteChannelConfig from '../components/WebsiteChannelConfig';
import WidgetSettings from '../components/WidgetSettings';
import { toast } from '../components/ui/use-toast';
import { API_BASE_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { cn } from '../lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Inbox {
  id: number;
  name: string;
  channel_type: string;
  account_id: number;
  ai_enabled: boolean;
  settings: Record<string, any>;
  description?: string;
  greeting_enabled: boolean;
  greeting_message?: string;
  working_hours_enabled: boolean;
  out_of_office_message?: string;
  auto_assignment_enabled: boolean;
  lock_to_single_conversation: boolean;
  conversations_count: number;
  created_at: string;
  updated_at: string;
  api_channel_id?: number;
  email_channel_id?: number;
  whatsapp_channel_id?: number;
  custom_gpt_channel_id?: number;
}

interface InboxStats {
  total_conversations: number;
  open_conversations: number;
  pending_conversations: number;
  resolved_conversations: number;
  avg_first_response_time?: number;
  avg_resolution_time?: number;
}

// ── Channel config ────────────────────────────────────────────────────────────

const channelTypes = [
  { value: 'website',    label: 'Website Widget', Icon: Globe },
  { value: 'api',        label: 'API Channel',    Icon: Plug },
  { value: 'email',      label: 'Email',          Icon: Mail },
  { value: 'whatsapp',   label: 'WhatsApp',       Icon: MessageSquare },
  { value: 'facebook',   label: 'Facebook',       Icon: Globe },
  { value: 'twitter',    label: 'Twitter',        Icon: Globe },
  { value: 'instagram',  label: 'Instagram',      Icon: Globe },
  { value: 'sms',        label: 'SMS',            Icon: Phone },
  { value: 'custom_gpt', label: 'Custom GPT',     Icon: Bot },
] as const;

const getChannelLabel = (channelType: string) =>
  channelTypes.find((ct) => ct.value === channelType)?.label ?? channelType;

const getChannelIcon = (channelType: string) => {
  const Icon = channelTypes.find((ct) => ct.value === channelType)?.Icon ?? MessageSquare;
  return <Icon className="h-4 w-4" />;
};

// ── Stat tiles ────────────────────────────────────────────────────────────────

interface StatTileProps {
  label: string;
  value: number | string;
  icon: React.ElementType;
  iconClass: string;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, icon: Icon, iconClass }) => (
  <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
    <CardHeader>
      <CardDescription>{label}</CardDescription>
      <CardTitle className="text-2xl font-semibold tabular-nums">{value}</CardTitle>
      <CardAction>
        <div className={cn('rounded-full bg-muted p-2', iconClass)}>
          <Icon className="h-4 w-4" />
        </div>
      </CardAction>
    </CardHeader>
  </Card>
);

// ── Skeleton row ──────────────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <TableRow>
    <TableCell>
      <div className="flex items-center gap-2.5">
        <Skeleton className="h-8 w-8 rounded-full shrink-0" />
        <div className="space-y-1.5">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </TableCell>
    <TableCell><Skeleton className="h-5 w-24 rounded-full" /></TableCell>
    <TableCell><Skeleton className="h-3.5 w-16" /></TableCell>
    <TableCell className="text-center"><Skeleton className="h-5 w-9 rounded-full mx-auto" /></TableCell>
    <TableCell><div className="flex gap-1"><Skeleton className="h-5 w-16 rounded-full" /><Skeleton className="h-5 w-20 rounded-full" /></div></TableCell>
    <TableCell><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
    <TableCell />
  </TableRow>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const InboxesPage: React.FC = () => {
  // ── List state ─────────────────────────────────────────────────────────────
  const [inboxes, setInboxes]         = useState<Inbox[]>([]);
  const [loading, setLoading]         = useState(true);
  const [searchText, setSearchText]   = useState('');
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(10);

  // ── Dialog state ───────────────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen]               = useState(false);
  const [editingInbox, setEditingInbox]           = useState<Inbox | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen]   = useState(false);
  const [inboxToDelete, setInboxToDelete]         = useState<Inbox | null>(null);
  const [statsOpen, setStatsOpen]                 = useState(false);
  const [selectedInboxStats, setSelectedInboxStats] = useState<InboxStats | null>(null);
  const [statsInboxName, setStatsInboxName]       = useState('');

  const [showWhatsAppConfig, setShowWhatsAppConfig]     = useState(false);
  const [showInstagramConfig, setShowInstagramConfig]   = useState(false);
  const [showAPIConfig, setShowAPIConfig]               = useState(false);
  const [showEmailConfig, setShowEmailConfig]           = useState(false);
  const [showFacebookConfig, setShowFacebookConfig]     = useState(false);
  const [showSMSConfig, setShowSMSConfig]               = useState(false);
  const [showCustomGPTConfig, setShowCustomGPTConfig]   = useState(false);
  const [showWebsiteConfig, setShowWebsiteConfig]       = useState(false);
  const [showWidgetSettings, setShowWidgetSettings]     = useState(false);
  const [selectedInboxForWidget, setSelectedInboxForWidget] = useState<Inbox | null>(null);
  const [selectedChannelType, setSelectedChannelType]   = useState<string>('');

  const [formData, setFormData] = useState({
    name: '',
    channel_type: 'website',
    description: '',
    greeting_enabled: true,
    greeting_message: 'Hello! How can we help you today?',
    working_hours_enabled: false,
    out_of_office_message: '',
    auto_assignment_enabled: true,
    lock_to_single_conversation: false,
    ai_enabled: true,
    settings: {} as Record<string, any>,
  });

  // ── Load ───────────────────────────────────────────────────────────────────

  useEffect(() => { loadInboxes(); }, []);

  const loadInboxes = async () => {
    try {
      setLoading(true);
      const response = await inboxesAPI.getInboxes();
      setInboxes(response.inboxes);
    } catch {
      toast({ title: 'Error', description: 'Failed to load inboxes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filteredInboxes = useMemo(() => {
    let list = inboxes;
    if (channelFilter !== 'all') {
      list = list.filter((i) => i.channel_type === channelFilter);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(
        (i) => i.name.toLowerCase().includes(q) || (i.description ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [inboxes, channelFilter, searchText]);

  const filteredTotal = filteredInboxes.length;

  const totalPages = useMemo(() => {
    if (filteredTotal <= 0) return 1;
    return Math.max(1, Math.ceil(filteredTotal / rowsPerPage));
  }, [filteredTotal, rowsPerPage]);

  const paginatedInboxes = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredInboxes.slice(start, start + rowsPerPage);
  }, [filteredInboxes, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchText, channelFilter]);

  useEffect(() => {
    if (filteredTotal <= 0) return;
    const maxPage = totalPages - 1;
    if (page > maxPage) setPage(maxPage);
  }, [page, filteredTotal, totalPages]);

  const goTo = (p: number) => {
    setPage(Math.max(0, Math.min(p, totalPages - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Derived stats ──────────────────────────────────────────────────────────

  const totalConversations = useMemo(
    () => inboxes.reduce((sum, i) => sum + i.conversations_count, 0),
    [inboxes],
  );
  const activeChannelTypes = useMemo(
    () => new Set(inboxes.map((i) => i.channel_type)).size,
    [inboxes],
  );
  const autoAssignCount = useMemo(
    () => inboxes.filter((i) => i.auto_assignment_enabled).length,
    [inboxes],
  );

  // ── CRUD ───────────────────────────────────────────────────────────────────

  const handleCreateInbox = async () => {
    try {
      const response = await inboxesAPI.createInbox(formData);
      setInboxes((prev) => [...prev, response]);
      setDialogOpen(false);
      resetForm();
      toast({ title: 'Inbox created', description: `${formData.name} was created successfully.` });
      if (formData.channel_type === 'website') {
        setSelectedInboxForWidget(response);
        setShowWidgetSettings(true);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to create inbox', variant: 'destructive' });
    }
  };

  const handleUpdateInbox = async () => {
    if (!editingInbox) return;
    try {
      const response = await inboxesAPI.updateInbox(editingInbox.id, formData);
      setInboxes((prev) => prev.map((i) => (i.id === editingInbox.id ? response : i)));
      setDialogOpen(false);
      setEditingInbox(null);
      resetForm();
      toast({ title: 'Inbox updated', description: `${formData.name} was updated successfully.` });
    } catch {
      toast({ title: 'Error', description: 'Failed to update inbox', variant: 'destructive' });
    }
  };

  const handleDeleteInbox = async () => {
    if (!inboxToDelete) return;
    try {
      await inboxesAPI.deleteInbox(inboxToDelete.id);
      setInboxes((prev) => prev.filter((i) => i.id !== inboxToDelete.id));
      toast({ title: 'Inbox deleted', description: `${inboxToDelete.name} has been removed.` });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete inbox', variant: 'destructive' });
    } finally {
      setDeleteDialogOpen(false);
      setInboxToDelete(null);
    }
  };

  const openDeleteDialog = (inbox: Inbox) => {
    setInboxToDelete(inbox);
    setDeleteDialogOpen(true);
  };

  const handleViewStats = async (inbox: Inbox) => {
    try {
      const response = await inboxesAPI.getInboxStats(inbox.id);
      setSelectedInboxStats(response);
      setStatsInboxName(inbox.name);
      setStatsOpen(true);
    } catch {
      toast({ title: 'Error', description: 'Failed to load inbox stats', variant: 'destructive' });
    }
  };

  // ── Channel selection + edit routing ──────────────────────────────────────

  const openCreateDialog = () => {
    resetForm();
    setEditingInbox(null);
    setSelectedChannelType('');
    setDialogOpen(true);
  };

  const handleChannelTypeSelect = (channelType: string) => {
    setDialogOpen(false);
    setSelectedChannelType(channelType);
    switch (channelType) {
      case 'website':    setShowWebsiteConfig(true);    break;
      case 'whatsapp':   setShowWhatsAppConfig(true);   break;
      case 'instagram':  setShowInstagramConfig(true);  break;
      case 'api':
      case 'API':        setShowAPIConfig(true);        break;
      case 'email':      setShowEmailConfig(true);      break;
      case 'facebook':   setShowFacebookConfig(true);   break;
      case 'sms':        setShowSMSConfig(true);        break;
      case 'custom_gpt': setShowCustomGPTConfig(true);  break;
      default:
        setFormData((prev) => ({ ...prev, channel_type: channelType }));
        setDialogOpen(true);
    }
  };

  const handleChannelCreated = (channel: any, channelType: string) => {
    setInboxes((prev) => [...prev, channel]);
    closeAllConfigs();
    toast({
      title: 'Channel created',
      description: `${channelType.charAt(0).toUpperCase() + channelType.slice(1)} channel created successfully.`,
    });
    loadInboxes();
  };

  const closeAllConfigs = () => {
    setShowWhatsAppConfig(false);
    setShowInstagramConfig(false);
    setShowAPIConfig(false);
    setShowEmailConfig(false);
    setShowCustomGPTConfig(false);
    setShowFacebookConfig(false);
    setShowSMSConfig(false);
    setShowWebsiteConfig(false);
    setShowWidgetSettings(false);
    setEditingInbox(null);
    setSelectedInboxForWidget(null);
  };

  const handleWebsiteChannelCreated  = (c: any) => handleChannelCreated(c, 'website');
  const handleWhatsAppChannelCreated = (c: any) => handleChannelCreated(c, 'whatsapp');
  const handleInstagramChannelCreated = (c: any) => handleChannelCreated(c, 'instagram');
  const handleAPIChannelCreated      = (c: any) => handleChannelCreated(c, 'API');
  const handleEmailChannelCreated    = (c: any) => handleChannelCreated(c, 'email');
  const handleFacebookChannelCreated = (c: any) => handleChannelCreated(c, 'facebook');
  const handleSMSChannelCreated      = (c: any) => handleChannelCreated(c, 'SMS');
  const handleCustomGPTChannelCreated = (c: any) => handleChannelCreated(c, 'custom_gpt');

  const openEditDialog = (inbox: Inbox) => {
    setEditingInbox(inbox);
    switch (inbox.channel_type) {
      case 'whatsapp':   setShowWhatsAppConfig(true);   break;
      case 'instagram':  setShowInstagramConfig(true);  break;
      case 'api':
      case 'API':        setShowAPIConfig(true);        break;
      case 'email':      setShowEmailConfig(true);      break;
      case 'facebook':   setShowFacebookConfig(true);   break;
      case 'sms':        setShowSMSConfig(true);        break;
      case 'custom_gpt': setShowCustomGPTConfig(true);  break;
      default:
        setFormData({
          name: inbox.name,
          channel_type: inbox.channel_type,
          description: inbox.description || '',
          greeting_enabled: inbox.greeting_enabled,
          greeting_message: inbox.greeting_message || '',
          working_hours_enabled: inbox.working_hours_enabled,
          out_of_office_message: inbox.out_of_office_message || '',
          auto_assignment_enabled: inbox.auto_assignment_enabled,
          lock_to_single_conversation: inbox.lock_to_single_conversation,
          ai_enabled: inbox.ai_enabled ?? true,
          settings: inbox.settings || {},
        });
        setDialogOpen(true);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      channel_type: 'website',
      description: '',
      greeting_enabled: true,
      greeting_message: 'Hello! How can we help you today?',
      working_hours_enabled: false,
      out_of_office_message: '',
      auto_assignment_enabled: true,
      lock_to_single_conversation: false,
      ai_enabled: true,
      settings: {},
    });
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ── AI toggle (unchanged direct-fetch pattern) ─────────────────────────────

  const handleToggleAI = async (inboxId: number, currentValue: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/inboxes/${inboxId}/toggle-ai`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ai_enabled: !currentValue }),
        },
      );
  
      if (response.ok) {
        const result = await response.json();
        setInboxes((prev) =>
          prev.map((inbox) =>
            inbox.id === inboxId ? { ...inbox, ai_enabled: result.ai_enabled } : inbox,
          ),
        );
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to toggle AI', variant: 'destructive' });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Inboxes</h1>
          <p className="text-sm text-muted-foreground">Manage your communication channels</p>
        </div>
        <Button size="sm" onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-1.5" />
          Create Inbox
        </Button>
      </div>

      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Total Inboxes"       value={inboxes.length}     icon={Inbox}       iconClass="text-primary" />
        <StatTile label="Total Conversations" value={totalConversations} icon={MessageSquare} iconClass="text-sky-500" />
        <StatTile label="Active Channels"     value={activeChannelTypes} icon={Layers}      iconClass="text-violet-500" />
        <StatTile label="Auto Assignment"     value={autoAssignCount}    icon={UserCheck}   iconClass="text-emerald-500" />
      </div>

      {/* ── Table card ──────────────────────────────────────────────────── */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>All Inboxes</CardTitle>
          <CardDescription>
            {searchText.trim() || channelFilter !== 'all'
              ? `${filteredTotal} of ${inboxes.length} inbox${inboxes.length !== 1 ? 'es' : ''}`
              : `${inboxes.length} inbox${inboxes.length !== 1 ? 'es' : ''}`}
          </CardDescription>
          <CardAction className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-8 pl-8 w-[160px] text-sm"
              />
            </div>
            {/* Channel filter */}
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="h-8 w-[148px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All channels</SelectItem>
                {channelTypes.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardAction>
        </CardHeader>

        <CardContent className="p-0 pb-2">
          <div className="max-h-[min(70vh,40rem)] overflow-y-auto overscroll-y-contain border-b border-border [&>div]:overflow-visible">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
              <TableRow>
                <TableHead className="pl-6 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Name
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Channel
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Conversations
                </TableHead>
                <TableHead className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  AI
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Features
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Status
                </TableHead>
                <TableHead className="pr-4 w-28" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
              ) : filteredInboxes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="flex flex-col items-center justify-center py-14 text-center">
                      <div className="rounded-full bg-muted p-4 mb-3 ring-1 ring-border">
                        <Inbox className="h-7 w-7 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {searchText || channelFilter !== 'all'
                          ? 'No inboxes match your filters'
                          : 'No inboxes yet'}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        {searchText || channelFilter !== 'all'
                          ? 'Try adjusting your search or filter'
                          : 'Create your first inbox to start receiving conversations'}
                      </p>
                      {!searchText && channelFilter === 'all' && (
                        <Button size="sm" className="mt-4" onClick={openCreateDialog}>
                          <Plus className="h-4 w-4 mr-1.5" />
                          Create Inbox
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedInboxes.map((inbox) => (
                  <TableRow key={inbox.id} className="group">

                    {/* Name */}
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {getChannelIcon(inbox.channel_type)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium leading-tight">{inbox.name}</p>
                          {inbox.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                              {inbox.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    {/* Channel */}
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {getChannelLabel(inbox.channel_type)}
                      </Badge>
                    </TableCell>

                    {/* Conversations */}
                    <TableCell>
                      <span className="text-sm tabular-nums">{inbox.conversations_count}</span>
                    </TableCell>

                    {/* AI toggle */}
                    <TableCell className="text-center">
                      <Switch
                        checked={inbox.ai_enabled ?? false}
                        onCheckedChange={() => handleToggleAI(inbox.id, inbox.ai_enabled ?? false)}
                        aria-label={inbox.ai_enabled ? 'Disable AI' : 'Enable AI'}
                      />
                    </TableCell>

                    {/* Features */}
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {inbox.greeting_enabled && (
                          <Badge variant="secondary" className="text-xs">Greeting</Badge>
                        )}
                        {inbox.working_hours_enabled && (
                          <Badge variant="secondary" className="text-xs">Hours</Badge>
                        )}
                        {inbox.auto_assignment_enabled && (
                          <Badge variant="secondary" className="text-xs">Auto Assign</Badge>
                        )}
                      </div>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Badge variant="success" className="text-xs">Active</Badge>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="pr-4">
                      <div className="flex items-center justify-end gap-1">
                        {/* Hover quick-actions */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`View stats for ${inbox.name}`}
                          onClick={() => handleViewStats(inbox)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {inbox.channel_type === 'website' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Widget settings"
                            onClick={() => { setSelectedInboxForWidget(inbox); setShowWidgetSettings(true); }}
                          >
                            <Settings className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label={`Edit ${inbox.name}`}
                            onClick={() => openEditDialog(inbox)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Overflow dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewStats(inbox)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Stats
                            </DropdownMenuItem>
                            {inbox.channel_type === 'website' ? (
                              <DropdownMenuItem onClick={() => { setSelectedInboxForWidget(inbox); setShowWidgetSettings(true); }}>
                                <Settings className="h-4 w-4 mr-2" />
                                Widget Settings
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => openEditDialog(inbox)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => openDeleteDialog(inbox)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border shrink-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="hidden sm:inline">Rows per page</span>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(v) => {
                  setRowsPerPage(parseInt(v, 10));
                  setPage(0);
                }}
              >
                <SelectTrigger className="h-7 w-[72px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="tabular-nums">
                {filteredTotal === 0
                  ? '0 of 0'
                  : `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredTotal)} of ${filteredTotal.toLocaleString()}`}
              </span>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(0)} disabled={page === 0 || filteredTotal === 0}>
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(page - 1)} disabled={page === 0 || filteredTotal === 0}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="min-w-[5rem] text-center text-xs tabular-nums">
                  Page {page + 1} of {totalPages}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(page + 1)} disabled={page + 1 >= totalPages || filteredTotal === 0}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(totalPages - 1)} disabled={page + 1 >= totalPages || filteredTotal === 0}>
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Create / Edit Dialog ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={() => setDialogOpen(false)}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInbox ? 'Edit Inbox' : 'Create New Inbox'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!selectedChannelType && !editingInbox ? (
              /* ── Channel type picker ─────────────────────────────────── */
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Select channel type</p>
                <div className="grid grid-cols-3 gap-3">
                  {channelTypes.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      onClick={() => handleChannelTypeSelect(value)}
                      className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent hover:border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-xs font-medium text-center leading-tight">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* ── Edit form ───────────────────────────────────────────── */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="inbox-name">Inbox Name *</Label>
                  <Input
                    id="inbox-name"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Enter inbox name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Channel Type</Label>
                  <Select
                    value={formData.channel_type}
                    onValueChange={(v) => handleFormChange('channel_type', v)}
                    disabled={!!editingInbox}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {channelTypes.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {((selectedChannelType && selectedChannelType !== 'whatsapp') ||
                  (editingInbox && editingInbox.channel_type !== 'whatsapp')) && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="inbox-description">Description</Label>
                      <textarea
                        id="inbox-description"
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                        rows={2}
                        value={formData.description}
                        onChange={(e) => handleFormChange('description', e.target.value)}
                        placeholder="Optional description"
                      />
                    </div>

                    <Separator />
                    <p className="text-sm font-semibold">Settings</p>

                    {/* Greeting */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="greeting-enabled" className="cursor-pointer">
                          Greeting Message
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Show a greeting to new visitors
                        </p>
                      </div>
                      <Switch
                        id="greeting-enabled"
                        checked={formData.greeting_enabled}
                        onCheckedChange={(v) => handleFormChange('greeting_enabled', v)}
                      />
                    </div>
                    {formData.greeting_enabled && (
                      <textarea
                        className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                        rows={2}
                        value={formData.greeting_message}
                        onChange={(e) => handleFormChange('greeting_message', e.target.value)}
                        placeholder="Greeting message…"
                      />
                    )}

                    {/* Working hours */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="working-hours" className="cursor-pointer">
                          Working Hours
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Limit availability to business hours
                        </p>
                      </div>
                      <Switch
                        id="working-hours"
                        checked={formData.working_hours_enabled}
                        onCheckedChange={(v) => handleFormChange('working_hours_enabled', v)}
                      />
                    </div>
                    {formData.working_hours_enabled && (
                      <textarea
                        className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                        rows={2}
                        value={formData.out_of_office_message}
                        onChange={(e) => handleFormChange('out_of_office_message', e.target.value)}
                        placeholder="Out-of-office message…"
                      />
                    )}

                    {/* Auto assignment */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="auto-assignment" className="cursor-pointer">
                          Auto Assignment
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Automatically assign conversations to agents
                        </p>
                      </div>
                      <Switch
                        id="auto-assignment"
                        checked={formData.auto_assignment_enabled}
                        onCheckedChange={(v) => handleFormChange('auto_assignment_enabled', v)}
                      />
                    </div>

                    {/* Lock single */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="lock-single" className="cursor-pointer">
                          Lock to Single Conversation
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          One active conversation per contact
                        </p>
                      </div>
                      <Switch
                        id="lock-single"
                        checked={formData.lock_to_single_conversation}
                        onCheckedChange={(v) => handleFormChange('lock_to_single_conversation', v)}
                      />
                    </div>

                    <Separator />
                    <p className="text-sm font-semibold">AI Settings</p>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="ai-enabled" className="cursor-pointer">
                          Enable AI Responses
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          New conversations will have AI responses turned on by default
                        </p>
                      </div>
                      <Switch
                        id="ai-enabled"
                        checked={formData.ai_enabled}
                        onCheckedChange={(v) => handleFormChange('ai_enabled', v)}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            {(selectedChannelType || editingInbox) && (
              <Button onClick={editingInbox ? handleUpdateInbox : handleCreateInbox}>
                {editingInbox ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation Dialog ───────────────────────────────────── */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Inbox
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-medium text-foreground">{inboxToDelete?.name}</span>?
            This will remove all associated settings. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteInbox}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Stats Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={statsOpen} onOpenChange={() => setStatsOpen(false)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Stats — {statsInboxName}</DialogTitle>
          </DialogHeader>
          {selectedInboxStats && (
            <div className="grid grid-cols-2 gap-3 py-2">
              {[
                { label: 'Total',    value: selectedInboxStats.total_conversations,   color: 'text-foreground' },
                { label: 'Open',     value: selectedInboxStats.open_conversations,    color: 'text-sky-600' },
                { label: 'Pending',  value: selectedInboxStats.pending_conversations, color: 'text-amber-600' },
                { label: 'Resolved', value: selectedInboxStats.resolved_conversations, color: 'text-emerald-600' },
              ].map(({ label, value, color }) => (
                <div
                  key={label}
                  className="flex flex-col gap-1 rounded-lg border border-border bg-muted/30 p-4"
                >
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {label}
                  </span>
                  <span className={cn('text-2xl font-semibold tabular-nums', color)}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Channel Configuration Dialogs (logic unchanged) ──────────────── */}
      {showWebsiteConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <WebsiteChannelConfig onChannelCreated={handleWebsiteChannelCreated} onClose={closeAllConfigs} />
          </DialogContent>
        </Dialog>
      )}

      {showWhatsAppConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="flex max-h-[90vh] w-full max-w-4xl flex-col gap-0 overflow-y-auto p-0 sm:rounded-lg">
            <div className="p-6">
              <WhatsAppChannelConfig onChannelCreated={handleWhatsAppChannelCreated} editingChannel={editingInbox} onCancel={closeAllConfigs} />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showInstagramConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <InstagramChannelConfig editingChannel={editingInbox} onCancel={closeAllConfigs} />
          </DialogContent>
        </Dialog>
      )}

      {showAPIConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <APIChannelConfig
              editMode={!!editingInbox}
              channelId={editingInbox?.api_channel_id}
              fallbackChannelId={editingInbox?.id}
              onSave={() => { loadInboxes(); closeAllConfigs(); }}
              onCancel={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}

      {showEmailConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <EmailChannelConfig channelId={editingInbox?.email_channel_id} onClose={closeAllConfigs} />
          </DialogContent>
        </Dialog>
      )}

      {showFacebookConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <FacebookChannelConfig editingChannel={editingInbox} onCancel={closeAllConfigs} />
          </DialogContent>
        </Dialog>
      )}

      {showSMSConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <SMSChannelConfig editingChannel={editingInbox} onCancel={closeAllConfigs} />
          </DialogContent>
        </Dialog>
      )}

      {showCustomGPTConfig && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <CustomGPTChannelConfig onChannelCreated={handleCustomGPTChannelCreated} editingChannel={editingInbox} onCancel={closeAllConfigs} />
          </DialogContent>
        </Dialog>
      )}

      {showWidgetSettings && selectedInboxForWidget && (
        <Dialog open onOpenChange={closeAllConfigs}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Widget Settings — {selectedInboxForWidget.name}</DialogTitle>
            </DialogHeader>
            <WidgetSettings inboxId={selectedInboxForWidget.id} onClose={closeAllConfigs} />
            <DialogFooter>
              <Button variant="outline" onClick={closeAllConfigs}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
};

export default InboxesPage;
